import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const CATEGORIES = ['Rent', 'Utilities', 'Insurance', 'Salaries & Labor', 'Software & Subscriptions', 'Marketing', 'Vehicle & Fleet', 'Loan Payments', 'Other'];
const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif';
const INP = { width: '100%', padding: '11px 14px', border: 'none', borderRadius: 10, fontSize: 15, outline: 'none', background: '#F2F2F7', color: '#1C1C1E', fontFamily: FONT, boxSizing: 'border-box' };
const LBL = { display: 'block', fontSize: 11, fontWeight: 600, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8, fontFamily: FONT };

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function ordinal(n) {
  const s = ['th','st','nd','rd'], v = n % 100;
  return n + (s[(v-20)%10] || s[v] || s[0]);
}

export default function RecurringModal({ item, onSave, onClose }) {
  const [form, setForm] = useState({ name: '', amount: '', frequency: 'monthly', category: 'Other', notes: '', active: true, dueDay: '', dueMonth: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (item) setForm({ ...item, amount: String(item.amount), dueDay: item.dueDay ?? '', dueMonth: item.dueMonth ?? '' });
  }, [item]);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!form.amount || isNaN(parseFloat(form.amount))) { setError('Valid amount is required.'); return; }
    const dueDay = form.dueDay !== '' ? parseInt(form.dueDay) : null;
    const dueMonth = form.dueMonth !== '' ? parseInt(form.dueMonth) : null;
    if (dueDay !== null && (dueDay < 1 || dueDay > 31)) { setError('Due day must be 1–31.'); return; }
    setSaving(true); setError('');
    try {
      await onSave({ ...form, amount: parseFloat(form.amount), dueDay, dueMonth });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 460, overflow: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.28)', fontFamily: FONT }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1C1C1E', margin: 0 }}>{item ? 'Edit Overhead' : 'Add Overhead'}</h2>
          <button onClick={onClose} style={{ background: '#F2F2F7', border: 'none', cursor: 'pointer', borderRadius: 50, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8E8E93' }}>
            <X size={15} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '0 24px 24px' }}>
          {error && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '10px 14px', color: '#DC2626', fontSize: 13, marginBottom: 14 }}>{error}</div>}

          <div style={{ marginBottom: 14 }}>
            <label style={LBL}>Name</label>
            <input type="text" value={form.name} onChange={e => set('name', e.target.value)} required style={INP} placeholder="e.g. Office Rent" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={LBL}>Amount ($)</label>
              <input type="number" min="0" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} required style={INP} placeholder="0.00" />
            </div>
            <div>
              <label style={LBL}>Frequency</label>
              <select value={form.frequency} onChange={e => set('frequency', e.target.value)} style={INP}>
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
              </select>
            </div>
          </div>

          {/* Due date fields */}
          {form.frequency === 'monthly' && (
            <div style={{ marginBottom: 14 }}>
              <label style={LBL}>Due Day of Month</label>
              <input
                type="number" min="1" max="31" value={form.dueDay}
                onChange={e => set('dueDay', e.target.value)}
                style={INP} placeholder="e.g. 1 for the 1st"
              />
              {form.dueDay && parseInt(form.dueDay) >= 1 && parseInt(form.dueDay) <= 31 && (
                <div style={{ fontSize: 11, color: '#8E8E93', marginTop: 5 }}>Due on the {ordinal(parseInt(form.dueDay))} of each month</div>
              )}
            </div>
          )}

          {form.frequency === 'annual' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={LBL}>Due Month</label>
                <select value={form.dueMonth} onChange={e => set('dueMonth', e.target.value)} style={INP}>
                  <option value="">— Select —</option>
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div>
                <label style={LBL}>Due Day</label>
                <input
                  type="number" min="1" max="31" value={form.dueDay}
                  onChange={e => set('dueDay', e.target.value)}
                  style={INP} placeholder="e.g. 15"
                />
              </div>
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <label style={LBL}>Category</label>
            <select value={form.category} onChange={e => set('category', e.target.value)} style={INP}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={LBL}>Notes (optional)</label>
            <input type="text" value={form.notes} onChange={e => set('notes', e.target.value)} style={INP} placeholder="Optional notes" />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <div onClick={() => set('active', !form.active)} style={{ width: 44, height: 26, borderRadius: 13, background: form.active ? '#34C759' : '#E5E5EA', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
              <div style={{ width: 22, height: 22, borderRadius: 11, background: 'white', position: 'absolute', top: 2, left: form.active ? 20 : 2, transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.25)' }} />
            </div>
            <span style={{ fontSize: 14, color: '#1C1C1E', fontWeight: 500 }}>Active — included in overhead calculations</span>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '11px 20px', border: '1px solid #E5E5EA', borderRadius: 12, background: 'white', color: '#374151', cursor: 'pointer', fontSize: 14, fontWeight: 500, fontFamily: FONT }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '11px 24px', border: 'none', borderRadius: 12, background: '#84CC16', color: 'white', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600, opacity: saving ? 0.8 : 1, fontFamily: FONT }}>
              {saving ? 'Saving…' : (item ? 'Update' : 'Add')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
